import { Player } from '../types/game';

export type NextGameParticipationMap = Record<string, boolean> | null | undefined;

export interface NextGameParticipant {
  id: string;
  name: string;
  role: 'player' | 'viewer';
  status: 'active' | 'left';
  isBot: boolean;
  isHost: boolean;
}

// Shared between the GameOver screen and the post-game lobby so the two
// participant lists never drift apart for the same room.
export function deriveNextGameParticipants(
  players: Player[],
  spectators: Array<{ id: string; name: string }> | undefined,
  options: { currentClientPlayerId: string | null; isCurrentClientHost: boolean },
): NextGameParticipant[] {
  const participantMap = new Map<string, NextGameParticipant>();
  players.forEach((player) => {
    participantMap.set(player.id, {
      id: player.id,
      name: player.name,
      role: player.role === 'viewer' ? 'viewer' : 'player',
      status: player.status || 'active',
      isBot: player.isBot === true,
      isHost: player.isHost === true,
    });
  });
  (spectators || []).forEach((spectator) => {
    if (participantMap.has(spectator.id)) return;
    participantMap.set(spectator.id, {
      id: spectator.id,
      name: spectator.name,
      role: 'viewer',
      status: 'active',
      isBot: false,
      isHost: spectator.id === options.currentClientPlayerId && options.isCurrentClientHost,
    });
  });
  return [...participantMap.values()]
    .filter((participant) => participant.status === 'active')
    .sort((left, right) => {
      if (left.isHost !== right.isHost) return left.isHost ? -1 : 1;
      if (left.role !== right.role) return left.role === 'player' ? -1 : 1;
      return left.name.localeCompare(right.name, 'pl');
    });
}

// The bool contract is intentionally minimal: only explicit true entries are stored.
// Missing keys mean "not participating in the next round".
export function normalizeNextGameParticipation(
  input: NextGameParticipationMap,
): Record<string, boolean> {
  if (!input) return {};

  const normalized: Record<string, boolean> = {};
  for (const [playerId, participating] of Object.entries(input)) {
    if (participating === true) {
      normalized[playerId] = true;
    }
  }
  return normalized;
}